import { useI18n, CONTACT, LOGO_TRANSPARENT, TRUSTED_BY } from "@/lib/i18n";
import { Mail, MessageCircle, Instagram } from "lucide-react";

export default function Footer() {
  const { t } = useI18n();
  const year = new Date().getFullYear();
  return (
    <footer
      className="bg-ink-900 text-sand-50 border-t border-sand-50/10"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
        <div className="md:col-span-6 flex flex-col">
          <img
            src={LOGO_TRANSPARENT}
            alt="Villa Reset Ibiza"
            className="w-36 md:w-44 mb-6 select-none pointer-events-none"
            draggable={false}
          />
          <p className="text-sand-50/55 text-[13px] md:text-sm max-w-sm leading-relaxed">
            {t.footer.tagline}
          </p>
          <p className="text-[10px] tracking-[0.4em] uppercase text-gold mt-5">
            {t.footer.location}
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">Contact</div>
          <ul className="flex flex-col gap-2.5 text-[13px]">
            <li>
              <a
                href={`https://wa.me/${CONTACT.whatsappRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-sand-50/75 hover:text-gold"
                data-testid="footer-whatsapp-link"
              >
                <MessageCircle size={14} strokeWidth={1.2} />
                {CONTACT.whatsappDisplay}
              </a>
            </li>
            <li>
              <a
                href={`mailto:${CONTACT.email}`}
                className="inline-flex items-center gap-3 text-sand-50/75 hover:text-gold"
                data-testid="footer-email-link"
              >
                <Mail size={14} strokeWidth={1.2} />
                {CONTACT.email}
              </a>
            </li>
            <li>
              <a
                href={CONTACT.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-sand-50/75 hover:text-gold"
                data-testid="footer-instagram-link"
              >
                <Instagram size={14} strokeWidth={1.2} />
                {CONTACT.instagram}
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">Navigate</div>
          <ul className="flex flex-col gap-2.5 text-[13px]">
            {[
              { id: "about", key: "about" },
              { id: "services", key: "services" },
              { id: "experience", key: "experience" },
              { id: "journal", key: "instagram" },
              { id: "contact", key: "contact" },
            ].map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className="text-sand-50/75 hover:text-gold"
                  data-testid={`footer-nav-${item.id}`}
                >
                  {t.nav[item.key]}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {TRUSTED_BY.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-10 md:pb-12" data-testid="footer-trusted-by">
          <div className="border-t border-sand-50/10 pt-8 md:pt-10 flex flex-col items-center gap-5 md:gap-6 text-center">
            <span className="text-[9.5px] tracking-[0.45em] uppercase text-gold/80">
              — Trusted by —
            </span>
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3 md:gap-x-8">
              {TRUSTED_BY.map((name, i) => (
                <span key={name} className="inline-flex items-center gap-x-5 md:gap-x-8">
                  {i > 0 && (
                    <span
                      className="inline-block w-1 h-1 rounded-full bg-sand-50/25"
                      aria-hidden="true"
                    />
                  )}
                  <span className="font-serif italic text-[15px] md:text-base text-sand-50/55 tracking-wide">
                    {name}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 border-t border-sand-50/10 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-[10px] tracking-[0.3em] uppercase text-sand-50/40">
        <span>© {year} Villa Reset Ibiza</span>
        <span>{t.footer.rights}</span>
      </div>
    </footer>
  );
}
